import { SystemTypes } from "spark-core-dx/services";

export const WIZARD_ITEM = "ResourceConfigurationWizard";
export const INTEGRATION_ITEM = "Integration";
export const SOURCE_SYSTEM_ITEM = "SourceSystem";
export const SOURCE_OBJECT_ITEM = "SourceObject";
export const SOURCE_OBJECT_TARGET_ITEM = "SourceObjectTarget";
export const CONNECTION_CHECK_OPERATION = "ConnectionCheck";
export const LOAD_SOURCE_SYSTEM_TARGETS_OPERATION = "LoadSourceSystemTargets";
export const LOAD_SOURCE_OBJECTS_OPERATION = "LoadSourceObjects";
export const SOURCE_OBJECT_MANAGE_PANEL_KEY = "sourceobjectmanage";
export const MARKETPLACE_RESOURCE_PANEL_KEY = "marketplaceaddresource";
export const MARKETPLACE_RESOURCE_PANEL_ROUTE = "/marketplace/resource/";
export const MANAGE_SOURCE_OBJECT_OPERATION = "ManageSourceObject";
export const INTEGRATION_MODE_EXISTING = "existing";
export const INTEGRATION_MODE_NEW = "new";
export const SOURCE_SYSTEM_MODE_EXISTING = "existing";
export const SOURCE_SYSTEM_MODE_NEW = "new";
export const INTEGRATION_EXISTING_FORM_GROUP_ID = "resourceWizardIntegrationExistingV2";
export const INTEGRATION_NEW_FORM_GROUP_ID = "resourceWizardIntegrationNewV2";
export const SOURCE_SYSTEM_EXISTING_FORM_GROUP_ID = "resourceWizardSourceSystemExistingV2";
export const SOURCE_SYSTEM_NEW_FORM_GROUP_ID = "resourceWizardSourceSystemNewV2";
export const RESOURCE_DETAILS_FORM_GROUP_ID = "resourceWizardResourceDetailsV2";
export const SOURCE_OBJECT_FORM_GROUP_ID = "resourceWizardSourceObjectV2";
export const SOURCE_SYSTEM_TYPE_TSQL = 1;
export const SOURCE_SYSTEM_TYPE_MONGODB = 2;
export const SOURCE_SYSTEM_TYPE_SNOWFLAKE = 5;
export const SOURCE_SYSTEM_TYPE_GOOGLEBIGQUERY = 7;
export const SOURCE_SYSTEM_TYPE_GREATPLAINS = 28;
export const SOURCE_SYSTEM_TYPE_PROGRESS_OPENEDGE = 29;
export const SOURCE_SYSTEM_TYPE_EDI = 21;
export const INTEGRATION_TYPE_ONEDRIVE = 14;
export const INTEGRATION_TYPE_FTP = 15;
export const INTEGRATION_TYPE_GOOGLEDRIVE = 17;
export const INTEGRATION_TYPE_DROPBOX = 18;
export const INTEGRATION_TYPE_AZURESTORAGE = 19;
export const INTEGRATION_TYPE_OPENTEXT_TRADINGGRID = 25;
export const INTEGRATION_TYPE_AZURE_LOGIC_APPS_AS2 = 29;
export const GREATPLAINS_DEFAULT_CREATED_DATE_FIELD = "CREATDDT";
export const GREATPLAINS_DEFAULT_UPDATED_DATE_FIELD = "MODIFDT";
export const SOURCE_OBJECT_TYPE_QUERY = 4;
export const SOURCE_OBJECT_TARGET_METADATA_ID = "86609dc1-1519-40ce-84f3-b767c319b485";
export const WIZARD_VALIDATION_ERROR_FLAG = "__resourceWizardValidationError";
export const EDI_TRANSACTION_SET_CUSTOM_FIELD = "EDITransactionSet";
export const EDI_PARENT_MESSAGE_TYPE_CUSTOM_FIELD = "ParentMessageType";

const COMPLETED_STATUS_VALUE = 3;
const ACTIVE_CONFIGURATION_STATUS = 4;
const CONFIGURED_CONFIGURATION_STATUS = 7;
const DEFAULT_HIDDEN_SETUP_FIELDS = new Set([
  "_id",
  "id",
  "uniqueid",
  "clientid",
  "client",
  "isdeleted",
  "createddate",
  "createdby",
  "updateddate",
  "updatedby",
  "customfields",
  "securecustomfields",
  "futurefeatures"
]);

const FIELD_OVERRIDE_KEYS = [
  "DisplayName",
  "Title",
  "Description",
  "PlaceHolder",
  "Placeholder",
  "DataJson",
  "IsRequired",
  "IsReadOnly",
  "IsHidden",
  "GroupId",
  "TabId",
  "SortOrder"
];

export const readValue = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

export const getValue = readValue;

const lowerFirst = (value = "") => value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : "";

const isTruthyMetadataFlag = (value) => (
  value === true
  || value === 1
  || (typeof value === "string" && ["true", "1", "yes"].includes(value.trim().toLowerCase()))
);

export const parseSetupFieldStateList = (...values) => {
  const result = [];
  const append = (value) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach(append);
      return;
    }
    String(value)
      .split(/[,;\n\r]+/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => result.push(entry));
  };

  values.forEach(append);
  return result;
};

const buildFieldStateTokenSet = (...values) => {
  const exact = new Set();
  const compact = new Set();
  parseSetupFieldStateList(...values).forEach((value) => {
    const normalized = normalizeText(value);
    if (!normalized) return;
    exact.add(normalized);
    compact.add(normalized.replace(/[\s_-]+/g, ""));
  });
  return { exact, compact };
};

const getFieldIdentityValues = (field) => ([
  readValue(field, "FieldName", "fieldName"),
  readValue(field, "Name", "name"),
  readValue(field, "Id", "id"),
  readValue(field, "FieldId", "fieldId"),
  readValue(field, "UIFieldId", "uiFieldId"),
  readValue(field, "UniqueId", "uniqueId"),
  readValue(field, "_id")
]).filter((value) => value !== undefined && value !== null && value !== "");

const fieldMatchesStateTokens = (field, tokenSet) => {
  if (!tokenSet || (!tokenSet.exact.size && !tokenSet.compact.size)) return false;
  return getFieldIdentityValues(field).some((value) => {
    const normalized = normalizeText(value);
    return tokenSet.exact.has(normalized)
      || tokenSet.compact.has(normalized.replace(/[\s_-]+/g, ""));
  });
};

const readMetadataStateList = (source, stateKey) => {
  const camelKey = lowerFirst(stateKey);
  return readValue(source, stateKey, camelKey)
    ?? readValue(source?.CustomFields, stateKey, camelKey)
    ?? readValue(source?.customFields, stateKey, camelKey);
};

export const getSetupFormFieldStateOverrides = (defaults = {}, formKey = "") => {
  const allStates = readValue(defaults, "setupFormFieldStateOverrides", "SetupFormFieldStateOverrides", "setupFormFieldStates", "SetupFormFieldStates") || {};
  const formStates = readValue(allStates, formKey, formKey.toLowerCase()) || {};
  return {
    IsReadOnly: readMetadataStateList(formStates, "IsReadOnly"),
    IsHidden: readMetadataStateList(formStates, "IsHidden")
  };
};

export const getId = (item) => readValue(item, "_id", "id", "UniqueId", "uniqueId") || "";

const hasQueryParamValue = (params, ...keys) =>
  keys.some((key) => params?.[key] !== undefined && params?.[key] !== null && params?.[key] !== "");

export const markWizardValidationError = (error) => {
  if (!error || typeof error !== "object") return error;
  error[WIZARD_VALIDATION_ERROR_FLAG] = true;
  error.isWizardValidationError = true;
  return error;
};

export const buildWizardValidationError = (message, options = {}) => {
  const error = markWizardValidationError(new Error(message || "Complete the required fields before continuing."));
  if (options.suppressSummary) error.suppressWizardValidationSummary = true;
  return error;
};

export const isWizardValidationError = (error) =>
  Boolean(error?.isWizardValidationError
    || error?.[WIZARD_VALIDATION_ERROR_FLAG]
    || error?.validationErrors
    || error?.validationIssues);

export const buildResourceOperationQueryParams = ({
  queryParams,
  clientResourceId,
  resourceConfigurationId,
  item,
  wizard,
  resourceConfiguration
} = {}) => {
  const scopedQueryParams = { ...(queryParams || {}) };
  const resolvedClientResourceId = clientResourceId
    || readValue(item, "ClientResourceId", "clientResourceId")
    || readValue(wizard, "ClientResourceId", "clientResourceId")
    || readValue(resourceConfiguration, "ClientResourceId", "clientResourceId")
    || "";
  const resolvedResourceConfigurationId = resourceConfigurationId
    || readValue(item, "ResourceConfigurationId", "resourceConfigurationId")
    || readValue(wizard, "ResourceConfigurationId", "resourceConfigurationId")
    || getId(resourceConfiguration)
    || "";

  if (resolvedClientResourceId && !hasQueryParamValue(scopedQueryParams, "clientResourceId", "ClientResourceId", "clientresourceid")) {
    scopedQueryParams.clientResourceId = resolvedClientResourceId;
  }

  if (resolvedResourceConfigurationId && !hasQueryParamValue(scopedQueryParams, "resourceConfigurationId", "ResourceConfigurationId", "resourceconfigurationid")) {
    scopedQueryParams.resourceConfigurationId = resolvedResourceConfigurationId;
  }

  return scopedQueryParams;
};

export const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value.toArray === "function") return value.toArray();
  return [];
};

export const firstOf = (value) => {
  if (!value) return null;
  if (typeof value.first === "function") return value.first();
  return Array.isArray(value) ? value[0] : null;
};

export const getFirst = firstOf;
export const toArray = asArray;

export const parseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const stringifyJson = (value) => JSON.stringify(value || {});

export const getDisplayName = (item) =>
  readValue(item, "DisplayName", "displayName", "Name", "name", "Title", "title") || "";

export const shallowEqualObjects = (left = {}, right = {}) => {
  if (left === right) return true;
  if (!left || !right || typeof left !== "object" || typeof right !== "object") return false;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length && leftKeys.every((key) => left[key] === right[key]);
};

export const keepObjectStateWhenEqual = (previous, next) => (shallowEqualObjects(previous, next) ? previous : next);

export const getOperationResult = (result) => firstOf(result) || result || null;

export const getOperationItem = (result) => {
  const operationResult = getOperationResult(result);
  return firstOf(operationResult?.Items)
    || firstOf(operationResult?.items)
    || operationResult?.Item
    || operationResult?.item
    || operationResult?.Value
    || operationResult?.value
    || null;
};

export const getOperationItems = (result) => {
  const operationResult = getOperationResult(result);
  const operationItems = [
    ...asArray(operationResult?.Items),
    ...asArray(operationResult?.items)
  ];
  if (operationResult === result) {
    return operationItems.filter(Boolean);
  }
  return [
    ...operationItems,
    ...asArray(result?.Items),
    ...asArray(result?.items)
  ].filter(Boolean);
};

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

export const resolveSourceTargetMetadata = (metadataSource) => {
  const metadataItems = getOperationItems(metadataSource);
  return metadataItems.find((item) =>
    normalizeText(getId(item)) === normalizeText(SOURCE_OBJECT_TARGET_METADATA_ID)
    || normalizeText(readValue(item, "UIItemId", "uiItemId", "UniqueId", "uniqueId")) === normalizeText(SOURCE_OBJECT_TARGET_METADATA_ID)
  ) || null;
};

const numericSequence = (item) => {
  const sequence = Number(readValue(
    item,
    "DisplaySequence",
    "displaySequence",
    "Sequence",
    "sequence",
    "StepSequence",
    "stepSequence",
    "SectionStepSequence",
    "sectionStepSequence",
    "SortOrder",
    "sortOrder"
  ));
  return Number.isFinite(sequence) && sequence > 0 ? sequence : Number.MAX_SAFE_INTEGER;
};

export const orderSetupItems = (items) => asArray(items)
  .map((item, index) => ({ item, index }))
  .sort((left, right) => numericSequence(left.item) - numericSequence(right.item) || left.index - right.index)
  .map(({ item }) => item);

export const stepKeysEqual = (left, right) => normalizeText(left) === normalizeText(right);

export const isCompletedStatus = (value) => value === COMPLETED_STATUS_VALUE || normalizeText(value) === "completed";

export const isActiveConfiguration = (configuration) => {
  const status = readValue(configuration, "ConfigurationStatus", "configurationStatus");
  const normalized = normalizeText(status);
  return status === ACTIVE_CONFIGURATION_STATUS
    || status === CONFIGURED_CONFIGURATION_STATUS
    || normalized === "active"
    || normalized === "configured";
};

export const getSteps = (wizard) => orderSetupItems(readValue(readValue(wizard, "SetupPath", "setupPath"), "Steps", "steps"));
export const getRunSteps = (wizard) => orderSetupItems(readValue(readValue(wizard, "SetupRun", "setupRun"), "Steps", "steps"));
export const getStepKey = (step) => readValue(step, "StepKey", "stepKey") || "";
export const getStepLabel = (step) => {
  const key = getStepKey(step);
  if (stepKeysEqual(key, "estimate-review")) return "Review";
  return readValue(step, "Label", "label") || key;
};
export const isBasicsStepKey = (stepKey) => stepKeysEqual(stepKey, "basics");
export const getSectionSteps = (step) => orderSetupItems(readValue(step, "SectionSteps", "sectionSteps"));
export const getSectionStepKey = (sectionStep) => readValue(sectionStep, "SectionStepKey", "sectionStepKey") || "";
export const getSectionStepLabel = (sectionStep) => {
  const label = readValue(sectionStep, "Label", "label") || getSectionStepKey(sectionStep);
  return stepKeysEqual(getSectionStepKey(sectionStep), "source-targets") && label === "EDI Document Targets"
    ? "EDI Targets"
    : label;
};
export const getSectionStepUiPatternKey = (sectionStep) => readValue(sectionStep, "UiPatternKey", "uiPatternKey") || "Unknown";
export const getSectionStepInputFields = (sectionStep, step) => {
  const sectionFields = asArray(readValue(sectionStep, "InputFields", "inputFields"));
  return sectionFields.length ? sectionFields : asArray(readValue(step, "InputFields", "inputFields"));
};

export const sectionStepExpectsRecord = (sectionStep, recordName) =>
  asArray(readValue(sectionStep, "ExpectedRecords", "expectedRecords"))
    .some((value) => normalizeText(value) === normalizeText(recordName));

export const getCurrentStepKey = (wizard) =>
  readValue(wizard, "CurrentStepKey", "currentStepKey")
  || readValue(readValue(wizard, "SetupRun", "setupRun"), "CurrentStepKey", "currentStepKey")
  || getStepKey(getSteps(wizard)[0]);

export const getCurrentSectionStepKey = (wizard, stepKey = getCurrentStepKey(wizard)) => {
  const step = getSteps(wizard).find((candidate) => stepKeysEqual(getStepKey(candidate), stepKey));
  const sectionSteps = getSectionSteps(step);
  const explicit = readValue(wizard, "CurrentSectionStepKey", "currentSectionStepKey")
    || readValue(readValue(wizard, "SetupRun", "setupRun"), "CurrentSectionStepKey", "currentSectionStepKey")
    || "";
  if (sectionSteps.some((sectionStep) => stepKeysEqual(getSectionStepKey(sectionStep), explicit))) return explicit;
  return getSectionStepKey(sectionSteps[0]) || "";
};

export const getSectionStepIdentity = (stepKey, sectionStepKey) => `${stepKey || ""}::${sectionStepKey || ""}`;

export const getSectionStepKeyFromIdentity = (identity, stepKey) => {
  const [identityStepKey, sectionStepKey] = String(identity || "").split("::");
  return stepKeysEqual(identityStepKey, stepKey) ? sectionStepKey || "" : "";
};

export const getStepKeyFromIdentity = (identity) => String(identity || "").split("::")[0] || "";

export const getRunStep = (wizard, stepKey) =>
  getRunSteps(wizard).find((step) => stepKeysEqual(getStepKey(step), stepKey)) || null;

export const getRunSectionStep = (wizard, stepKey, sectionStepKey) =>
  orderSetupItems(readValue(getRunStep(wizard, stepKey), "SectionSteps", "sectionSteps"))
    .find((sectionStep) => stepKeysEqual(getSectionStepKey(sectionStep), sectionStepKey)) || null;

export const getRunSectionStepInputValues = (wizard, stepKey, sectionStepKey) => {
  const runSectionStep = getRunSectionStep(wizard, stepKey, sectionStepKey);
  if (runSectionStep) return parseJson(readValue(runSectionStep, "InputValuesJson", "inputValuesJson"), {});
  return parseJson(readValue(getRunStep(wizard, stepKey), "InputValuesJson", "inputValuesJson"), {});
};

export const isRunStepCompleted = (wizard, stepKey) =>
  isCompletedStatus(readValue(getRunStep(wizard, stepKey), "StepStatus", "stepStatus", "Status", "status"));

export const isRunSectionStepCompleted = (wizard, stepKey, sectionStepKey) =>
  isCompletedStatus(readValue(getRunSectionStep(wizard, stepKey, sectionStepKey), "StepStatus", "stepStatus", "Status", "status"));

export const isWizardSectionStepCurrent = (wizard, stepKey, sectionStepKey) =>
  stepKeysEqual(getCurrentStepKey(wizard), stepKey)
  && stepKeysEqual(getCurrentSectionStepKey(wizard, stepKey), sectionStepKey);

export const getStepIndex = (wizard, stepKey) =>
  getSteps(wizard).findIndex((step) => stepKeysEqual(getStepKey(step), stepKey));

export const getSectionStepIndex = (step, sectionStepKey) =>
  getSectionSteps(step).findIndex((sectionStep) => stepKeysEqual(getSectionStepKey(sectionStep), sectionStepKey));

export const getWorkflowStepIndex = (wizard) => getStepIndex(wizard, getCurrentStepKey(wizard));

export const getWorkflowSectionStepIndex = (wizard) => {
  const workflowStepKey = getCurrentStepKey(wizard);
  const workflowStep = getSteps(wizard).find((candidate) => stepKeysEqual(getStepKey(candidate), workflowStepKey));
  return getSectionStepIndex(workflowStep, getCurrentSectionStepKey(wizard, workflowStepKey));
};

export const isStepBeforeWorkflow = (wizard, stepKey) => {
  const workflowIndex = getWorkflowStepIndex(wizard);
  const targetIndex = getStepIndex(wizard, stepKey);
  return workflowIndex >= 0 && targetIndex >= 0 && targetIndex < workflowIndex;
};

export const isStepAfterWorkflow = (wizard, stepKey) => {
  const workflowIndex = getWorkflowStepIndex(wizard);
  const targetIndex = getStepIndex(wizard, stepKey);
  return workflowIndex >= 0 && targetIndex >= 0 && targetIndex > workflowIndex;
};

export const isStepAtWorkflow = (wizard, stepKey) =>
  stepKeysEqual(getCurrentStepKey(wizard), stepKey);

export const isSectionStepBeforeWorkflow = (wizard, stepKey, sectionStepKey) => {
  if (isStepBeforeWorkflow(wizard, stepKey)) return true;
  if (!isStepAtWorkflow(wizard, stepKey)) return false;
  const step = getSteps(wizard).find((candidate) => stepKeysEqual(getStepKey(candidate), stepKey));
  const workflowSectionIndex = getSectionStepIndex(step, getCurrentSectionStepKey(wizard, stepKey));
  const targetIndex = getSectionStepIndex(step, sectionStepKey);
  return workflowSectionIndex >= 0 && targetIndex >= 0 && targetIndex < workflowSectionIndex;
};

export const isSectionStepAfterWorkflow = (wizard, stepKey, sectionStepKey) => {
  if (isStepAfterWorkflow(wizard, stepKey)) return true;
  if (!isStepAtWorkflow(wizard, stepKey)) return false;
  const step = getSteps(wizard).find((candidate) => stepKeysEqual(getStepKey(candidate), stepKey));
  const workflowSectionIndex = getSectionStepIndex(step, getCurrentSectionStepKey(wizard, stepKey));
  const targetIndex = getSectionStepIndex(step, sectionStepKey);
  return workflowSectionIndex >= 0 && targetIndex >= 0 && targetIndex > workflowSectionIndex;
};

export const getSectionStepKeyForNavigation = (wizard, stepKey) => {
  const steps = getSteps(wizard);
  const step = steps.find((candidate) => stepKeysEqual(getStepKey(candidate), stepKey));
  const sectionSteps = getSectionSteps(step);
  if (!sectionSteps.length) return "";

  const workflowStepKey = getCurrentStepKey(wizard);
  const workflowStepIndex = getStepIndex(wizard, workflowStepKey);
  const targetStepIndex = getStepIndex(wizard, stepKey);
  if (targetStepIndex === workflowStepIndex) {
    return getCurrentSectionStepKey(wizard, stepKey);
  }
  if (targetStepIndex < workflowStepIndex) {
    return getSectionStepKey(sectionSteps[sectionSteps.length - 1]) || "";
  }
  return getSectionStepKey(sectionSteps[0]) || "";
};

export const getLocalWizardNavigationTarget = (wizard, stepKey, sectionStepKey, direction = "next") => {
  const steps = getSteps(wizard);
  const stepIndex = getStepIndex(wizard, stepKey);
  if (stepIndex < 0) return null;
  const sections = getSectionSteps(steps[stepIndex]);
  const sectionIndex = Math.max(0, getSectionStepIndex(steps[stepIndex], sectionStepKey));

  if (direction === "previous") {
    if (sectionIndex > 0) return { stepKey, sectionStepKey: getSectionStepKey(sections[sectionIndex - 1]) };
    const previousStep = steps[stepIndex - 1];
    if (!previousStep) return null;
    const previousSections = getSectionSteps(previousStep);
    return {
      stepKey: getStepKey(previousStep),
      sectionStepKey: getSectionStepKey(previousSections[previousSections.length - 1]) || ""
    };
  }

  if (sectionIndex < sections.length - 1) return { stepKey, sectionStepKey: getSectionStepKey(sections[sectionIndex + 1]) };
  const nextStep = steps[stepIndex + 1];
  if (!nextStep) return null;
  return {
    stepKey: getStepKey(nextStep),
    sectionStepKey: getSectionStepKeyForNavigation(wizard, getStepKey(nextStep))
  };
};

export const isFutureStep = (wizard, stepKey) => {
  return isStepAfterWorkflow(wizard, stepKey);
};

export const isWizardStepLocked = (wizard, stepKey) =>
  isFutureStep(wizard, stepKey);

export const isFutureSectionStep = (wizard, stepKey, sectionStepKey) => {
  return isSectionStepAfterWorkflow(wizard, stepKey, sectionStepKey);
};

export const isWizardSectionStepLocked = isFutureSectionStep;

export const getStepLetter = (index) => String.fromCharCode(65 + index);

export const getSectionStepNavigationState = (sectionSteps, wizard, stepKey, activeSectionStepKey) => {
  const orderedSectionSteps = asArray(sectionSteps);
  const activeIndex = orderedSectionSteps.findIndex((sectionStep) => stepKeysEqual(getSectionStepKey(sectionStep), activeSectionStepKey));
  const workflowStepKey = getCurrentStepKey(wizard);
  const workflowStepIndex = getStepIndex(wizard, workflowStepKey);
  const stepIndex = getStepIndex(wizard, stepKey);
  const workflowSectionStepKey = getCurrentSectionStepKey(wizard, workflowStepKey);
  const previousTarget = workflowStepKey
    ? getLocalWizardNavigationTarget(wizard, workflowStepKey, workflowSectionStepKey, "previous")
    : null;
  const previousTargetIndex = stepKeysEqual(previousTarget?.stepKey, stepKey)
    ? orderedSectionSteps.findIndex((sectionStep) => stepKeysEqual(getSectionStepKey(sectionStep), previousTarget.sectionStepKey))
    : -1;

  const workflowSectionKey = stepKeysEqual(workflowStepKey, stepKey)
    ? getCurrentSectionStepKey(wizard, stepKey)
    : "";
  const workflowCurrentIndex = orderedSectionSteps.findIndex((sectionStep) =>
    stepKeysEqual(getSectionStepKey(sectionStep), workflowSectionKey));

  return {
    activeIndex,
    workflowCurrentIndex,
    workflowStepIndex,
    stepIndex,
    editIndex: previousTargetIndex
  };
};

export const getSectionStepNavigationItems = (sectionSteps, wizard, stepKey, activeSectionStepKey) => {
  const orderedSectionSteps = asArray(sectionSteps);
  const navigationState = getSectionStepNavigationState(
    orderedSectionSteps,
    wizard,
    stepKey,
    activeSectionStepKey
  );

  return orderedSectionSteps.map((sectionStep, index) => {
    const sectionStepKey = getSectionStepKey(sectionStep);
    const completed = isSectionStepBeforeWorkflow(wizard, stepKey, sectionStepKey);
    const locked = isWizardSectionStepLocked(wizard, stepKey, sectionStepKey);
    const active = stepKeysEqual(sectionStepKey, activeSectionStepKey);
    const current = navigationState.workflowCurrentIndex >= 0 && index === navigationState.workflowCurrentIndex;
    const canEditPrevious = completed && index === navigationState.editIndex;
    return {
      sectionStep,
      sectionStepKey,
      completed,
      locked,
      active,
      current,
      canEditPrevious,
      showCompletedBadge: completed && !canEditPrevious && !current,
      disabled: locked
    };
  });
};

export const getProductResourceDefaults = (wizard) =>
  parseJson(readValue(readValue(wizard, "ProductResource", "productResource"), "SetupDefaultsJson", "setupDefaultsJson"), {});

export const getLinkedRecords = (wizard) =>
  asArray(readValue(readValue(wizard, "ResourceConfiguration", "resourceConfiguration"), "LinkedRecords", "linkedRecords"));

export const getLinkedRecord = (wizard, itemName, relationship) => getLinkedRecords(wizard).find((record) => {
  const recordItemName = readValue(record, "ItemName", "itemName");
  const recordRelationship = readValue(record, "Relationship", "relationship");
  return normalizeText(recordItemName) === normalizeText(itemName)
    && (!relationship || normalizeText(recordRelationship) === normalizeText(relationship));
}) || null;

export const getLinkedRecordItemId = (record) => readValue(record, "ItemId", "itemId") || "";

const normalizeLookupValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "object") {
    return normalizeLookupValue(readValue(value, "Value", "value", "Id", "id", "Key", "key"));
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

export const normalizeIntegrationTypeValue = normalizeLookupValue;
export const normalizeSourceSystemTypeValue = normalizeLookupValue;

export const resolveEffectiveIntegrationTypeValue = (integration = {}, preferredTypeValue = null) => {
  const normalizedPreferred = normalizeIntegrationTypeValue(preferredTypeValue);
  if (normalizedPreferred !== null) return normalizedPreferred;
  return normalizeIntegrationTypeValue(readValue(integration, "IntegrationType", "integrationType", "IntegrationTypeValue", "integrationTypeValue"));
};

export const isTsqlSourceSystem = (sourceSystem) => {
  const sourceSystemType = normalizeSourceSystemTypeValue(readValue(sourceSystem, "SourceSystemType", "sourceSystemType", "SourceSystemTypeKey", "sourceSystemTypeKey"));
  return sourceSystemType === SOURCE_SYSTEM_TYPE_TSQL || String(sourceSystemType || "").toUpperCase() === "TSQL";
};

const SQL_QUERY_TARGET_SOURCE_SYSTEM_TYPES = new Set([
  SOURCE_SYSTEM_TYPE_TSQL,
  // MongoDB is intentionally stubbed out until custom Mongo query-target execution is supported.
  // SOURCE_SYSTEM_TYPE_MONGODB,
  SOURCE_SYSTEM_TYPE_SNOWFLAKE,
  SOURCE_SYSTEM_TYPE_GOOGLEBIGQUERY,
  SOURCE_SYSTEM_TYPE_GREATPLAINS,
  SOURCE_SYSTEM_TYPE_PROGRESS_OPENEDGE
]);

const SQL_QUERY_TARGET_SOURCE_SYSTEM_KEYS = new Set([
  "TSQL",
  // "MONGODB",
  "SNOWFLAKE",
  "GOOGLEBIGQUERY",
  "GOOGLE_BIGQUERY",
  "GREATPLAINS",
  "GREAT_PLAINS",
  "PROGRESS_OPENEDGE",
  "PROGRESSOPENEDGE"
]);

export const isSqlQueryTargetSourceSystem = (sourceSystem) => {
  const rawType = readValue(sourceSystem, "SourceSystemType", "sourceSystemType", "SourceSystemTypeKey", "sourceSystemTypeKey");
  const sourceSystemType = normalizeSourceSystemTypeValue(rawType);
  if (SQL_QUERY_TARGET_SOURCE_SYSTEM_TYPES.has(sourceSystemType)) return true;

  const textCandidates = [
    rawType,
    readValue(sourceSystem, "SourceSystemTypeKey", "sourceSystemTypeKey"),
    readValue(sourceSystem, "SystemName", "systemName"),
    readValue(sourceSystem, "Name", "name")
  ];

  return textCandidates.some((candidate) => SQL_QUERY_TARGET_SOURCE_SYSTEM_KEYS.has(String(candidate || "").replace(/[^a-z0-9_]+/gi, "").toUpperCase()));
};

const readLookupValue = (value) =>
  readValue(value, "Value", "value", "Id", "id") ?? value;

const readLookupText = (value) =>
  readValue(value, "Text", "text", "DisplayName", "displayName", "Name", "name") ?? value;

export const isSourceObjectTypeQuery = (target) => {
  const objectTypeRaw = readValue(target, "ObjectType", "objectType", "ObjectTypeKey", "objectTypeKey");
  const objectTypeValue = normalizeLookupValue(readLookupValue(objectTypeRaw));
  const objectTypeText = normalizeText(readLookupText(objectTypeRaw)).replace(/[^a-z0-9]+/g, "");
  const systemObjectTypeText = normalizeText(readValue(target, "SystemObjectType", "systemObjectType")).replace(/[^a-z0-9]+/g, "");

  return objectTypeValue === SOURCE_OBJECT_TYPE_QUERY ||
    objectTypeText === "query" ||
    objectTypeText === "queryorfilter" ||
    systemObjectTypeText === "query";
};

export const resolveProductResourceIntegrationTypeValue = (defaults = {}) =>
  normalizeIntegrationTypeValue(readValue(defaults, "integrationTypeValue", "IntegrationTypeValue", "integrationTypeKey", "IntegrationTypeKey"));

export const resolveProductResourceSourceSystemTypeValue = (defaults = {}) =>
  normalizeSourceSystemTypeValue(readValue(defaults, "sourceSystemTypeValue", "SourceSystemTypeValue", "sourceSystemTypeKey", "SourceSystemTypeKey"));

export const filterIntegrationsByType = (integrations, integrationTypeValue) => {
  const normalizedType = normalizeIntegrationTypeValue(integrationTypeValue);
  if (normalizedType === null) return asArray(integrations);
  return asArray(integrations).filter((integration) =>
    normalizeIntegrationTypeValue(readValue(integration, "IntegrationType", "integrationType")) === normalizedType);
};

export const EDI_FILE_INTAKE_INTEGRATION_TYPES = new Set([
  INTEGRATION_TYPE_ONEDRIVE,
  INTEGRATION_TYPE_FTP,
  INTEGRATION_TYPE_GOOGLEDRIVE,
  INTEGRATION_TYPE_DROPBOX,
  INTEGRATION_TYPE_AZURESTORAGE,
  INTEGRATION_TYPE_OPENTEXT_TRADINGGRID,
  INTEGRATION_TYPE_AZURE_LOGIC_APPS_AS2
]);

export const isEdiSourceSystemSetup = (defaults = {}) => {
  const sourceSystemTypeValue = resolveProductResourceSourceSystemTypeValue(defaults);
  const sourceSystemTypeKey = readValue(defaults, "sourceSystemTypeKey", "SourceSystemTypeKey") || "";
  return sourceSystemTypeValue === SOURCE_SYSTEM_TYPE_EDI || String(sourceSystemTypeKey).toUpperCase() === "EDI";
};

export const getIntegrationSetupFilterKey = (defaults = {}) => {
  const integrationTypeValue = resolveProductResourceIntegrationTypeValue(defaults);
  if (integrationTypeValue !== null) return `type:${integrationTypeValue}`;
  return isEdiSourceSystemSetup(defaults) ? "edi-file-intake" : "all";
};

export const filterIntegrationsForSetup = (integrations, integrationTypeValue, defaults = {}) => {
  const typedIntegrations = filterIntegrationsByType(integrations, integrationTypeValue);
  if (normalizeIntegrationTypeValue(integrationTypeValue) !== null || !isEdiSourceSystemSetup(defaults)) {
    return typedIntegrations;
  }
  return typedIntegrations.filter((integration) =>
    EDI_FILE_INTAKE_INTEGRATION_TYPES.has(normalizeIntegrationTypeValue(readValue(integration, "IntegrationType", "integrationType"))));
};

export const buildIntegrationDraft = (values = {}, defaults = {}) => {
  const provider = readValue(defaults, "providerName", "ProviderName") || "Source";
  return {
    ...values,
    IsActive: true,
    IsOnline: true,
    Name: readValue(values, "IntegrationName", "integrationName", "Name", "name") || `${provider} Integration`,
    DisplayName: readValue(values, "IntegrationName", "integrationName", "DisplayName", "displayName") || `${provider} Integration`,
    Description: readValue(values, "IntegrationDescription", "integrationDescription", "Description", "description") || "",
    IntegrationType: resolveEffectiveIntegrationTypeValue(values, resolveProductResourceIntegrationTypeValue(defaults))
  };
};

export const buildIntegrationStepValues = ({ currentValues = {}, mode, integration, defaults, productResourceDefaults, connectionStatus = {} }) => {
  const setupDefaults = defaults || productResourceDefaults || {};
  return ({
  ...currentValues,
  IntegrationMode: mode,
  IntegrationId: getId(integration),
  IntegrationName: getDisplayName(integration),
  IntegrationType: resolveEffectiveIntegrationTypeValue(integration, resolveProductResourceIntegrationTypeValue(setupDefaults)),
  IntegrationTypeKey: readValue(setupDefaults, "integrationTypeKey", "IntegrationTypeKey") || "",
  DBName: getIntegrationDatabaseName(integration) || readValue(currentValues, "DBName", "dbName") || "",
  ConnectionCheckStatus: connectionStatus.ConnectionCheckStatus || "",
  ConnectionCheckMessage: connectionStatus.ConnectionCheckMessage || "",
  ConnectionCheckedUtc: connectionStatus.ConnectionCheckedUtc || ""
  });
};

export const getIntegrationDatabaseName = (integration = {}) => {
  const customFields = readValue(integration, "CustomFields", "customFields") || {};
  return readValue(
    integration,
    "DBName",
    "dbName",
    "DatabaseName",
    "databaseName",
    "CompanyDatabase",
    "companyDatabase"
  ) || readValue(
    customFields,
    "DBName",
    "dbName",
    "DatabaseName",
    "databaseName",
    "CompanyDatabase",
    "companyDatabase"
  ) || "";
};

export const buildSourceSystemDraft = (values = {}, defaults = {}, integrationId = "", profileId = "", integration = null) => {
  const provider = readValue(defaults, "providerName", "ProviderName") || "Source";
  const systemDefaults = readValue(defaults, "sourceSystemDefaultValues", "SourceSystemDefaultValues") || {};
  const sourceSystemType = resolveProductResourceSourceSystemTypeValue(defaults);
  const isGreatPlains = sourceSystemType === SOURCE_SYSTEM_TYPE_GREATPLAINS;
  const integrationDisplayName = getDisplayName(integration) || readValue(values, "IntegrationName", "integrationName") || "";
  const integrationDbName = getIntegrationDatabaseName(integration) || readValue(values, "DBName", "dbName") || "";
  const defaultDisplayName = integrationDisplayName
    ? `${integrationDisplayName} Source System`
    : readValue(systemDefaults, "DisplayName", "displayName", "Name", "name") || `${provider} Source System`;
  const defaultName = integrationDbName || readValue(systemDefaults, "Name", "name") || `${provider} Source System`;
  const defaultSystemName = integrationDbName
    || readValue(systemDefaults, "SystemName", "systemName")
    || defaultDisplayName.replace(/\s+/g, "");
  const defaultCreatedDateObjectFieldName = readValue(systemDefaults, "DefaultCreatedDateObjectFieldName", "defaultCreatedDateObjectFieldName")
    || (isGreatPlains ? GREATPLAINS_DEFAULT_CREATED_DATE_FIELD : "");
  const defaultUpdatedDateObjectFieldName = readValue(systemDefaults, "DefaultUpdatedDateObjectFieldName", "defaultUpdatedDateObjectFieldName")
    || (isGreatPlains ? GREATPLAINS_DEFAULT_UPDATED_DATE_FIELD : "");
  return {
    ...values,
    IsActive: true,
    IsOnline: true,
    Name: readValue(values, "SourceSystemName", "sourceSystemName", "Name", "name") || defaultName,
    DisplayName: readValue(values, "SourceSystemName", "sourceSystemName", "DisplayName", "displayName") || defaultDisplayName,
    SystemName: readValue(values, "SystemName", "systemName", "SourceSystemSystemName", "sourceSystemSystemName") || defaultSystemName,
    Description: readValue(values, "SourceSystemDescription", "sourceSystemDescription", "Description", "description") || readValue(systemDefaults, "Description", "description") || "",
    IntegrationId: integrationId || readValue(values, "IntegrationId", "integrationId") || "",
    SourceSystemType: sourceSystemType,
    ...(defaultCreatedDateObjectFieldName ? {
      DefaultCreatedDateObjectFieldName: readValue(values, "DefaultCreatedDateObjectFieldName", "defaultCreatedDateObjectFieldName") || defaultCreatedDateObjectFieldName
    } : {}),
    ...(defaultUpdatedDateObjectFieldName ? {
      DefaultUpdatedDateObjectFieldName: readValue(values, "DefaultUpdatedDateObjectFieldName", "defaultUpdatedDateObjectFieldName") || defaultUpdatedDateObjectFieldName
    } : {}),
    ProfileId: profileId || readValue(values, "ProfileId", "profileId") || ""
  };
};

export const buildSourceSystemStepValues = ({ currentValues = {}, sourceSystem, defaults, productResourceDefaults, integrationId = "" }) => {
  const setupDefaults = defaults || productResourceDefaults || {};
  return ({
  ...currentValues,
  SourceSystemId: getId(sourceSystem),
  SourceSystemName: getDisplayName(sourceSystem),
  SourceSystemType: resolveProductResourceSourceSystemTypeValue(setupDefaults),
  SourceSystemTypeKey: readValue(setupDefaults, "sourceSystemTypeKey", "SourceSystemTypeKey") || "",
  IntegrationId: integrationId || readValue(sourceSystem, "IntegrationId", "integrationId") || ""
  });
};

export const getSetupFormFieldOverrides = (defaults = {}, formKey = "") => {
  const allOverrides = readValue(defaults, "setupFormFieldOverrides", "SetupFormFieldOverrides") || {};
  return readValue(allOverrides, formKey, formKey.toLowerCase())
    || (formKey === "SourceSystem" ? readValue(defaults, "sourceSystemFieldOverrides", "SourceSystemFieldOverrides") : null)
    || {};
};

export const applyFieldOverrides = (field, override = {}) => {
  const next = { ...field };
  for (const key of FIELD_OVERRIDE_KEYS) {
    const value = readValue(override, key, key.charAt(0).toLowerCase() + key.slice(1));
    if (value !== undefined && value !== null) next[key] = value;
  }
  return next;
};

export const applySetupFormFieldOverrides = (metadata, overrides = {}) => {
  if (!metadata) return metadata;
  return {
    ...metadata,
    Fields: asArray(readValue(metadata, "Fields", "fields")).map((field) => {
      const fieldName = readValue(field, "FieldName", "fieldName") || "";
      const override = readValue(overrides, fieldName, fieldName.toLowerCase()) || {};
      return applyFieldOverrides(field, override);
    })
  };
};

export const buildSetupFormMetadata = (metadata, options = {}) => {
  if (!metadata) return null;
  const hidden = buildFieldStateTokenSet(
    Array.from(DEFAULT_HIDDEN_SETUP_FIELDS),
    options.hiddenFieldNames,
    options.IsHidden,
    readMetadataStateList(metadata, "IsHidden")
  );
  const readOnly = buildFieldStateTokenSet(
    options.readOnlyFieldNames,
    options.IsReadOnly,
    readMetadataStateList(metadata, "IsReadOnly")
  );
  const fields = asArray(readValue(metadata, "Fields", "fields")).filter((field) => {
    const fieldName = normalizeText(readValue(field, "FieldName", "fieldName"));
    if (!fieldName) return false;
    if (fieldMatchesStateTokens(field, hidden) || isTruthyMetadataFlag(readValue(field, "IsHidden", "isHidden", "Hidden", "hidden"))) return false;
    return readValue(field, "ShowInSetup", "showInSetup") !== false;
  }).map((field) => {
    if (fieldMatchesStateTokens(field, readOnly) || isTruthyMetadataFlag(readValue(field, "IsReadOnly", "isReadOnly", "ReadOnly", "readOnly"))) {
      return {
        ...field,
        IsReadOnly: true
      };
    }
    return field;
  });
  return { ...metadata, Fields: fields };
};

export const getMetadataFields = (metadata) =>
  asArray(readValue(metadata, "Fields", "fields")).filter((field) => readValue(field, "FieldName", "fieldName"));

const fieldTypeFromKey = (field) => {
  const explicit = readValue(field, "FieldType", "fieldType");
  if (explicit !== undefined && explicit !== null && explicit !== "") return explicit;
  const key = normalizeText(readValue(field, "FieldTypeKey", "fieldTypeKey", "ControlType", "controlType")).replace(/[\s_-]/g, "");
  if (["checkbox", "check", "bool", "boolean"].includes(key)) return SystemTypes.UIFieldType.CheckBox;
  if (["textarea", "textmulti", "multilinetext"].includes(key)) return SystemTypes.UIFieldType.TextArea;
  if (["select", "selectlist"].includes(key)) return SystemTypes.UIFieldType.SelectList;
  if (["searchselect", "searchselectlist"].includes(key)) return SystemTypes.UIFieldType.SearchSelectList;
  return SystemTypes.UIFieldType.TextBox;
};

export const buildResourceDetailsFieldMetadata = (field) => {
  const fieldName = readValue(field, "FieldName", "fieldName") || "";
  return {
    ...field,
    FieldName: fieldName,
    DisplayName: readValue(field, "DisplayName", "displayName") || fieldName,
    Description: readValue(field, "Description", "description") || "",
    FieldType: fieldTypeFromKey(field),
    Format: readValue(field, "Format", "format") || "",
    IsRequired: Boolean(readValue(field, "IsRequired", "isRequired")),
    ValidationType: readValue(field, "ValidationType", "validationType") || SystemTypes.UIFieldValidationType.None,
    DataJson: readValue(field, "DataJson", "dataJson", "Options", "options") || ""
  };
};

const valueMissing = (value) => {
  const resolvedValue = unwrapFormValue(value);
  if (resolvedValue === undefined || resolvedValue === null) return true;
  if (typeof resolvedValue === "string") return resolvedValue.trim() === "";
  if (Array.isArray(resolvedValue)) return resolvedValue.length === 0;
  return false;
};

const isTruthy = (value) => value === true || value === 1 || String(value ?? "").toLowerCase() === "true";

const readCaseInsensitiveValue = (item, key) => {
  if (!item || !key || typeof item !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(item, key)) return item[key];

  const normalizedKey = String(key).toLowerCase();
  const matchingKey = Object.keys(item).find((itemKey) => String(itemKey).toLowerCase() === normalizedKey);
  return matchingKey ? item[matchingKey] : undefined;
};

const unwrapFormValue = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;

  const explicitValue = readCaseInsensitiveValue(value, "Value");
  if (explicitValue !== undefined) return explicitValue;

  const textValue = readCaseInsensitiveValue(value, "Text");
  if (textValue !== undefined) return textValue;

  return value;
};

const getDraftFieldValue = (item, field) => {
  const fieldName = readValue(field, "FieldName", "fieldName");
  if (!fieldName) return undefined;

  const directValue = readCaseInsensitiveValue(item, fieldName);
  if (directValue !== undefined) return unwrapFormValue(directValue);

  const fieldContainers = [
    readValue(item, "CustomFields", "customFields"),
    readValue(item, "SecureCustomFields", "secureCustomFields"),
    readValue(item, "CustomFieldValues", "customFieldValues")
  ];

  for (const container of fieldContainers) {
    const containerValue = readCaseInsensitiveValue(container, fieldName);
    if (containerValue !== undefined) return unwrapFormValue(containerValue);

    for (const nestedValue of Object.values(container || {})) {
      if (!nestedValue || typeof nestedValue !== "object") continue;
      const nestedFieldValue = readCaseInsensitiveValue(nestedValue, fieldName);
      if (nestedFieldValue !== undefined) return unwrapFormValue(nestedFieldValue);
    }
  }

  return undefined;
};

const shouldValidateRequiredField = (field) => {
  if (!isTruthy(readValue(field, "IsRequired", "isRequired"))) return false;
  if (isTruthy(readValue(field, "IsHidden", "isHidden", "Hidden", "hidden"))) return false;
  if (isTruthy(readValue(field, "IsDeleted", "isDeleted"))) return false;
  if (readValue(field, "IsActive", "isActive") === false) return false;
  return true;
};

export const getMissingRequiredIntegrationFields = (item, fields = []) => asArray(fields)
  .filter(shouldValidateRequiredField)
  .filter((field) => valueMissing(getDraftFieldValue(item, field)))
  .map((field) => readValue(field, "DisplayName", "displayName", "FieldName", "fieldName") || "Required field");

export const formatRequiredFieldsMessage = (fieldNames = [], itemLabel = "Integration") => {
  const missingFields = asArray(fieldNames).filter(Boolean);
  if (!missingFields.length) return "";
  if (missingFields.length === 1) return `${missingFields[0]} is required.`;
  return `Missing required ${itemLabel} fields: ${missingFields.join(", ")}.`;
};

export const validateRequiredIntegrationFields = (item, fields = []) =>
  formatRequiredFieldsMessage(getMissingRequiredIntegrationFields(item, fields));

export const validateIntegrationConnectionString = (integration) => {
  const connectionString = readValue(integration, "ConnectionString", "connectionString");
  const secureConnectionString = readValue(integration, "SecureConnectionString", "secureConnectionString");
  return valueMissing(connectionString) && valueMissing(secureConnectionString) ? "Connection String is required." : "";
};

export const getSourceTargetKey = (target, index = 0) =>
  readValue(target, "SourceObjectTargetId", "sourceObjectTargetId", "UniqueId", "uniqueId", "_id", "id")
  || `${readValue(target, "ObjectName", "objectName", "DisplayName", "displayName") || "target"}-${index}`;

export const getSourceObjectKey = (sourceObject, index = 0) =>
  readValue(sourceObject, "SourceObjectId", "sourceObjectId", "UniqueId", "uniqueId", "_id", "id")
  || `${readValue(sourceObject, "SystemName", "systemName", "DisplayName", "displayName") || "source-object"}-${index}`;

export const normalizeEdiTransactionSetCode = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const text = String(value).trim().toUpperCase();
  const x12Match = text.match(/(?:^|[^0-9])(\d{3})(?:[^0-9]|$)/);
  return x12Match?.[1] || text;
};

export const resolveProductResourceEdiTransactionSets = (defaults = {}) => {
  const values = [];
  const append = (value) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach(append);
      return;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return;
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          JSON.parse(trimmed).forEach(append);
          return;
        } catch {
          // Fall through to delimiter parsing.
        }
      }
      const entries = trimmed.split(/[,;|]+/).map((entry) => entry.trim()).filter(Boolean);
      entries.forEach((entry) => values.push(entry));
      return;
    }
    values.push(value);
  };

  append(readValue(defaults, "defaultTransactionSets", "DefaultTransactionSets"));
  append(readValue(defaults, "defaultTransactionSet", "DefaultTransactionSet"));
  return Array.from(new Set(values.map(normalizeEdiTransactionSetCode).filter(Boolean)));
};

export const resolveSourceTargetEdiTransactionSet = (target = {}) => {
  const customFields = readValue(target, "CustomFields", "customFields") || {};
  const explicitValue = readValue(
    target,
    EDI_TRANSACTION_SET_CUSTOM_FIELD,
    EDI_PARENT_MESSAGE_TYPE_CUSTOM_FIELD,
    "TransactionSet",
    "transactionSet",
    "TransactionSetId",
    "transactionSetId"
  ) ?? readValue(
    customFields,
    EDI_TRANSACTION_SET_CUSTOM_FIELD,
    EDI_PARENT_MESSAGE_TYPE_CUSTOM_FIELD,
    "TransactionSet",
    "transactionSet",
    "TransactionSetId",
    "transactionSetId"
  );
  const normalizedExplicitValue = normalizeEdiTransactionSetCode(explicitValue);
  if (normalizedExplicitValue) return normalizedExplicitValue;

  const targetText = [
    readValue(target, "ObjectName", "objectName"),
    readValue(target, "SystemObjectType", "systemObjectType"),
    readValue(target, "DisplayName", "displayName")
  ].filter(Boolean).join(" ");
  return normalizeEdiTransactionSetCode(targetText);
};

export const filterSourceTargetsForProductDefaults = (targets = [], defaults = {}) => {
  const transactionSets = resolveProductResourceEdiTransactionSets(defaults);
  const ediFormat = String(readValue(defaults, "ediFormat", "EdiFormat") || "").toUpperCase();
  if (!transactionSets.length || (ediFormat && ediFormat !== "X12")) return targets;

  const transactionSetLookup = new Set(transactionSets);
  return asArray(targets).filter((target) => transactionSetLookup.has(resolveSourceTargetEdiTransactionSet(target)));
};

export const mergeFilteredSourceTargets = (allTargets = [], filteredTargets = []) => {
  const filteredByKey = new Map();
  asArray(filteredTargets).forEach((target, index) => {
    filteredByKey.set(String(getSourceTargetKey(target, index)), target);
  });

  const merged = asArray(allTargets).map((target, index) => {
    const key = String(getSourceTargetKey(target, index));
    if (!filteredByKey.has(key)) return target;
    const updatedTarget = filteredByKey.get(key);
    filteredByKey.delete(key);
    return updatedTarget;
  });

  filteredByKey.forEach((target) => merged.push(target));
  return merged;
};

const createClientGuid = () => {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  if (randomUuid) return randomUuid;

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
    const value = Math.floor(Math.random() * 16);
    return (token === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
};

const getQueryTargetAliasSeed = (existingTargets) => {
  const aliases = new Set(asArray(existingTargets)
    .map((target) => String(readValue(target, "ObjectName", "objectName") || "").toLowerCase())
    .filter(Boolean));
  let index = aliases.size + 1;
  let alias = `QueryTarget${index}`;
  while (aliases.has(alias.toLowerCase())) {
    index += 1;
    alias = `QueryTarget${index}`;
  }
  return { alias, index };
};

export const createSqlQueryTargetDraft = (sourceSystem = {}, existingTargets = [], overrides = {}) => {
  const sourceObjectTargetId = readValue(overrides, "SourceObjectTargetId", "sourceObjectTargetId", "UniqueId", "uniqueId") || createClientGuid();
  const { alias, index } = getQueryTargetAliasSeed(existingTargets);
  const displayName = `Query Target ${index}`;
  const separator = readValue(
    overrides,
    "ObjectUniqueIDSeparator",
    "objectUniqueIDSeparator"
  ) ?? readValue(sourceSystem, "DefaultObjectUniqueIDSeparator", "defaultObjectUniqueIDSeparator") ?? "|";

  return {
    SourceObjectTargetId: sourceObjectTargetId,
    UniqueId: sourceObjectTargetId,
    DisplayName: displayName,
    ObjectName: alias,
    ObjectType: SOURCE_OBJECT_TYPE_QUERY,
    SystemObjectType: "Query",
    IsCustomQuery: true,
    IsActive: true,
    Query: "",
    ParentObjectName: "",
    ObjectUniqueIDSeparator: separator,
    ...overrides
  };
};

export const createTsqlQueryTargetDraft = createSqlQueryTargetDraft;

const GENERATED_QUERY_TARGET_CUSTOM_FIELD_KEYS = [
  "BuiltInQueryTargetKey",
  "BuiltInQueryTargetVersion",
  "BuiltInQueryTargetCategory",
  "BuiltInQueryTargetSource",
  "BuiltInQueryTargetDescription"
];

export const cloneSqlQueryTargetDraft = (sourceSystem = {}, existingTargets = [], sourceTarget = {}) => {
  const sourceObjectTargetId = createClientGuid();
  const { alias } = getQueryTargetAliasSeed(existingTargets);
  const customFields = { ...(readValue(sourceTarget, "CustomFields", "customFields") || {}) };
  GENERATED_QUERY_TARGET_CUSTOM_FIELD_KEYS.forEach((key) => {
    delete customFields[key];
  });

  const displayNameBase = getDisplayName(sourceTarget)
    || readValue(sourceTarget, "ObjectName", "objectName")
    || "Query Target";

  return {
    ...sourceTarget,
    _id: undefined,
    id: undefined,
    SourceObjectTargetId: sourceObjectTargetId,
    sourceObjectTargetId,
    UniqueId: sourceObjectTargetId,
    uniqueId: sourceObjectTargetId,
    ObjectName: alias,
    objectName: alias,
    DisplayName: `${displayNameBase} Copy`,
    displayName: `${displayNameBase} Copy`,
    ObjectType: SOURCE_OBJECT_TYPE_QUERY,
    objectType: SOURCE_OBJECT_TYPE_QUERY,
    SystemObjectType: "Query",
    systemObjectType: "Query",
    IsCustomQuery: true,
    isCustomQuery: true,
    IsActive: true,
    isActive: true,
    CustomFields: customFields,
    customFields
  };
};

export const buildConfiguredSourceObjectMetadata = (metadata, setupFields = []) => {
  if (!metadata) return null;
  const configuredFields = asArray(setupFields);
  if (!configuredFields.length) return buildSetupFormMetadata(metadata);
  const byName = new Map(asArray(readValue(metadata, "Fields", "fields")).map((field) => [normalizeText(readValue(field, "FieldName", "fieldName")), field]));
  const fields = configuredFields.map((configuredField) => {
    const fieldName = readValue(configuredField, "FieldName", "fieldName") || "";
    const base = byName.get(normalizeText(fieldName)) || { FieldName: fieldName };
    return applyFieldOverrides({ ...base, FieldName: fieldName }, configuredField);
  }).filter((field) => normalizeText(readValue(field, "FieldName", "fieldName")) !== "clientid");
  return { ...metadata, Fields: fields };
};

export const buildSourceObjectCardFields = (fields = []) =>
  asArray(fields).filter((field) => readValue(field, "ShowOnCard", "showOnCard") === true).slice(0, 4);

export const buildSourceObjectValueDisplayResolvers = ({ sourceSystem }) => ({
  SourceSystemId: () => getDisplayName(sourceSystem) || readValue(sourceSystem, "Name", "name") || "",
  CanSyncData: (value) => (value ? "Yes" : "No"),
  IsActive: (value) => (value ? "Yes" : "No")
});

export const validateRequiredCardGridRows = (rows, fields, label) => {
  const requiredFields = asArray(fields).filter((field) => readValue(field, "IsRequired", "isRequired"));
  for (const row of asArray(rows)) {
    const missingField = requiredFields.find((field) => valueMissing(readValue(row, readValue(field, "FieldName", "fieldName"))));
    if (missingField) {
      return `${label || "Record"} ${getDisplayName(row) || getId(row)} is missing ${readValue(missingField, "DisplayName", "displayName", "FieldName", "fieldName")}.`;
    }
  }
  return "";
};

const sourceTargetMissingMessage = (row, label, fieldDisplayName) =>
  `${label || "Source Target"} ${getDisplayName(row) || getId(row) || "record"} is missing ${fieldDisplayName}.`;

export const validateSourceTargetRows = (rows, fields, label = "Source Target") => {
  const requiredValidationMessage = validateRequiredCardGridRows(rows, fields, label);
  if (requiredValidationMessage) return requiredValidationMessage;

  for (const row of asArray(rows)) {
    if (valueMissing(readValue(row, "ObjectName", "objectName"))) {
      return sourceTargetMissingMessage(row, label, "Object Name");
    }

    if (valueMissing(readValue(row, "ObjectType", "objectType"))) {
      return sourceTargetMissingMessage(row, label, "Object Type");
    }

    if (isSourceObjectTypeQuery(row) && valueMissing(readValue(row, "Query", "query"))) {
      return sourceTargetMissingMessage(row, label, "Search Query/Filter");
    }
  }

  return "";
};

export const getOperationName = (operation) =>
  readValue(operation, "Name", "name", "OperationTypeId", "operationTypeId", "Value", "value", "_id", "id") || "";

export const getOperationDisplayName = (operation) =>
  readValue(operation, "DisplayName", "displayName", "Text", "text") || getOperationName(operation);

export const buildOperationPayload = (wizard, overrides = {}) => ({
  ClientResourceId: overrides.ClientResourceId || readValue(wizard, "ClientResourceId", "clientResourceId") || "",
  ResourceConfigurationId: overrides.ResourceConfigurationId || readValue(wizard, "ResourceConfigurationId", "resourceConfigurationId") || getId(readValue(wizard, "ResourceConfiguration", "resourceConfiguration")),
  SetupRunId: overrides.SetupRunId || readValue(wizard, "SetupRunId", "setupRunId") || getId(readValue(wizard, "SetupRun", "setupRun")),
  CurrentStepKey: overrides.CurrentStepKey || getCurrentStepKey(wizard),
  CurrentSectionStepKey: overrides.CurrentSectionStepKey || getCurrentSectionStepKey(wizard, overrides.CurrentStepKey || getCurrentStepKey(wizard)),
  StepKey: overrides.StepKey || getCurrentStepKey(wizard),
  SectionStepKey: overrides.SectionStepKey || getCurrentSectionStepKey(wizard, overrides.StepKey || getCurrentStepKey(wizard)),
  StepInputValuesJson: overrides.StepInputValuesJson || "{}"
});

const publishValidationErrors = (coreState, validationErrors) => {
  if (!validationErrors || !Object.keys(validationErrors).length) return;
  const payload = { ...validationErrors, __updatedDate: new Date().toISOString() };
  if (typeof coreState?.merge === "function") coreState.merge(payload, "backendValidationErrors");
  else if (typeof coreState?.global?.set === "function") coreState.global.set(payload, "backendValidationErrors");
};

export const getValidationErrorSummary = (validationErrors) =>
  Object.entries(validationErrors || {})
    .filter(([key]) => key !== "__updatedDate")
    .map(([, value]) => readValue(value, "DisplayMessage", "displayMessage", "Info", "info"))
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(", ");

export const normalizeValidationSeverity = (severity) => {
  const normalized = normalizeText(severity).toLowerCase();
  return normalized === "warning" || normalized === "warn" ? "Warning" : "Error";
};

export const normalizeValidationIssue = (issue) => {
  if (!issue) return null;
  const message = readValue(issue, "Message", "message", "DisplayMessage", "displayMessage", "Content", "content");
  if (!message) return null;
  return {
    Severity: normalizeValidationSeverity(readValue(issue, "Severity", "severity")),
    Code: readValue(issue, "Code", "code") || "",
    StepKey: readValue(issue, "StepKey", "stepKey") || "",
    SectionStepKey: readValue(issue, "SectionStepKey", "sectionStepKey") || "",
    UiPatternKey: readValue(issue, "UiPatternKey", "uiPatternKey") || "",
    Label: readValue(issue, "Label", "label") || "",
    Message: message
  };
};

export const extractValidationIssues = (source) => {
  const result = getOperationResult(source);
  const item = getOperationItem(source);
  return asArray(readValue(source, "ValidationIssues", "validationIssues"))
    .concat(asArray(readValue(result, "ValidationIssues", "validationIssues")))
    .concat(asArray(readValue(item, "ValidationIssues", "validationIssues")))
    .map(normalizeValidationIssue)
    .filter(Boolean)
    .filter((issue, index, issues) =>
      issues.findIndex((candidate) =>
        candidate.Severity === issue.Severity
        && candidate.StepKey === issue.StepKey
        && candidate.SectionStepKey === issue.SectionStepKey
        && candidate.Message === issue.Message) === index);
};

export const hasBlockingValidationIssues = (issues) =>
  asArray(issues).some((issue) => normalizeValidationSeverity(readValue(issue, "Severity", "severity")) === "Error");

export const getValidationIssueSummary = (issues) =>
  asArray(issues)
    .map((issue) => readValue(issue, "Message", "message", "DisplayMessage", "displayMessage"))
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(", ");

export const buildClientValidationError = (fieldName, displayMessage, coreState, options = {}) => {
  const validationErrors = { [fieldName]: { DisplayMessage: displayMessage, Info: "" } };
  publishValidationErrors(coreState, validationErrors);
  const error = new Error(displayMessage);
  error.validationErrors = validationErrors;
  if (options.suppressSummary) error.suppressWizardValidationSummary = true;
  return markWizardValidationError(error);
};

export const extractServerValidationErrors = (source) => {
  const result = getOperationResult(source);
  const errors = readValue(source, "ValidationErrors", "validationErrors")
    || readValue(result, "ValidationErrors", "validationErrors")
    || readValue(readValue(source, "Message", "message"), "ValidationErrors", "validationErrors")
    || null;
  return errors && Object.keys(errors).length ? errors : null;
};

export const buildServerFailureError = (source, fallbackMessage, coreState) => {
  const validationErrors = extractServerValidationErrors(source);
  const validationSummary = getValidationErrorSummary(validationErrors);
  if (validationSummary) {
    publishValidationErrors(coreState, validationErrors);
    const error = new Error(validationSummary);
    error.validationErrors = validationErrors;
    return markWizardValidationError(error);
  }
  const validationIssues = extractValidationIssues(source);
  const validationIssueSummary = getValidationIssueSummary(validationIssues);
  if (validationIssueSummary) {
    const error = new Error(validationIssueSummary);
    error.validationIssues = validationIssues;
    error.wizardItem = getOperationItem(source);
    return markWizardValidationError(error);
  }
  const messageDetails = asArray(readValue(source, "MessageDetails", "messageDetails"))
    .concat(asArray(readValue(getOperationResult(source), "MessageDetails", "messageDetails")))
    .filter(Boolean)
    .join(", ");
  const message = readValue(source, "DisplayMessage", "displayMessage", "ErrorMessage", "errorMessage")
    || readValue(readValue(source, "Message", "message"), "DisplayMessage", "displayMessage", "Content", "content", "Info", "info")
    || messageDetails
    || fallbackMessage;
  return new Error(message);
};

export const getErrorSummary = (error, fallbackMessage) =>
  getValidationErrorSummary(error?.validationErrors)
  || getValidationIssueSummary(error?.validationIssues)
  || error?.message
  || fallbackMessage;

const GENERIC_SYSTEM_ERROR_MESSAGE = "A system error occurred. See details for more information.";

const normalizeConnectionMessage = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  if (typeof value === "object") {
    const nestedMessage = readValue(
      value,
      "DisplayMessage",
      "displayMessage",
      "ErrorMessage",
      "errorMessage",
      "Message",
      "message",
      "Content",
      "content",
      "Info",
      "info",
      "Detail",
      "detail",
      "Details",
      "details"
    );
    if (nestedMessage !== undefined && nestedMessage !== null && nestedMessage !== value) {
      return normalizeConnectionMessage(nestedMessage);
    }
  }

  return "";
};

const connectionMessageValues = (value) => {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value.toArray === "function") return value.toArray();
  return [value];
};

const appendConnectionMessageCandidates = (messages, value) => {
  connectionMessageValues(value).forEach((entry) => {
    const message = normalizeConnectionMessage(entry);
    if (message && !messages.includes(message)) messages.push(message);
  });
};

const isGenericConnectionMessage = (value) =>
  normalizeText(value) === normalizeText(GENERIC_SYSTEM_ERROR_MESSAGE);

export const getConnectionCheckFailureMessage = (error, fallbackMessage = "Connection check failed.") => {
  const result = getOperationResult(error);
  const nestedResult = getOperationResult(readValue(error, "Result", "result", "Response", "response", "Data", "data"));
  const candidates = [];
  [
    readValue(error, "ConnectionCheckMessage", "connectionCheckMessage"),
    readValue(result, "ConnectionCheckMessage", "connectionCheckMessage"),
    readValue(nestedResult, "ConnectionCheckMessage", "connectionCheckMessage"),
    readValue(error, "DebugErrorMessage", "debugErrorMessage", "ExceptionMessage", "exceptionMessage"),
    readValue(result, "DebugErrorMessage", "debugErrorMessage", "ExceptionMessage", "exceptionMessage"),
    readValue(nestedResult, "DebugErrorMessage", "debugErrorMessage", "ExceptionMessage", "exceptionMessage"),
    readValue(error, "MessageDetails", "messageDetails"),
    readValue(result, "MessageDetails", "messageDetails"),
    readValue(nestedResult, "MessageDetails", "messageDetails"),
    readValue(readValue(error, "Message", "message"), "MessageDetails", "messageDetails"),
    readValue(readValue(result, "Message", "message"), "MessageDetails", "messageDetails"),
    readValue(error, "DisplayMessage", "displayMessage", "ErrorMessage", "errorMessage"),
    readValue(result, "DisplayMessage", "displayMessage", "ErrorMessage", "errorMessage"),
    readValue(nestedResult, "DisplayMessage", "displayMessage", "ErrorMessage", "errorMessage"),
    readValue(readValue(error, "Message", "message"), "DisplayMessage", "displayMessage", "Content", "content", "Info", "info"),
    readValue(readValue(result, "Message", "message"), "DisplayMessage", "displayMessage", "Content", "content", "Info", "info"),
    error?.message
  ].forEach((value) => appendConnectionMessageCandidates(candidates, value));

  return candidates.find((message) => !isGenericConnectionMessage(message) && normalizeText(message) !== normalizeText(fallbackMessage))
    || candidates.find((message) => !isGenericConnectionMessage(message))
    || candidates[0]
    || fallbackMessage;
};

export const buildConnectionCheckFailureStatus = (error, fallbackMessage = "Connection check failed.") => {
  const status = error?.connectionStatus || error || {};
  const statusMessage = getConnectionCheckFailureMessage(status, fallbackMessage);
  const errorMessage = error?.connectionStatus
    ? getConnectionCheckFailureMessage(error, fallbackMessage)
    : statusMessage;
  const message = isGenericConnectionMessage(statusMessage) || normalizeText(statusMessage) === normalizeText(fallbackMessage)
    ? errorMessage
    : statusMessage;

  return {
    ConnectionCheckStatus: readValue(status, "ConnectionCheckStatus", "connectionCheckStatus") || "Failed",
    ConnectionCheckMessage: message,
    ConnectionCheckedUtc: readValue(status, "ConnectionCheckedUtc", "connectionCheckedUtc") || new Date().toISOString()
  };
};

export const formatConnectionCheckStatusText = ({
  status,
  message,
  prefix,
  successText = "Success"
} = {}) => {
  const normalizedStatus = String(status || "").trim();
  if (!normalizedStatus) return "";
  if (normalizeText(normalizedStatus) === "success") {
    return [prefix, successText].filter(Boolean).join(" ");
  }
  return [
    prefix,
    `${normalizedStatus}${message ? `: ${message}` : ""}`
  ].filter(Boolean).join(" ");
};

export const copyTextToClipboard = async (text) => {
  if (!text) return;

  const fallbackCopy = () => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    if (!copied) {
      throw new Error("Copy command was blocked.");
    }
  };

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      fallbackCopy();
      return;
    }
  }

  fallbackCopy();
};

export const requireServiceResponse = (response, fallbackMessage = "Service call did not return a response.") => {
  if (response === undefined || response === null) {
    throw new Error(`${fallbackMessage} Sign in again and retry.`);
  }
  return response;
};

export const requireSuccessfulServiceResponse = (response, fallbackMessage, coreState) => {
  requireServiceResponse(response, fallbackMessage);
  const operationResult = getOperationResult(response);
  const operationSuccess = readValue(operationResult, "Success", "success");
  const rawSuccess = readValue(response, "Success", "success");
  const failed = operationSuccess === false
    || rawSuccess === false
    || String(operationSuccess).toLowerCase() === "false"
    || String(rawSuccess).toLowerCase() === "false";
  if (failed) {
    throw buildServerFailureError(operationResult || response, fallbackMessage, coreState);
  }
  return response;
};

export const buildWizardAiPanelContext = ({ wizard, clientResourceId, resourceConfigurationId }) => {
  const configurationId = resourceConfigurationId
    || readValue(wizard, "ResourceConfigurationId", "resourceConfigurationId")
    || getId(readValue(wizard, "ResourceConfiguration", "resourceConfiguration"))
    || clientResourceId
    || "";
  return {
    referenceId: configurationId,
    referenceName: "ResourceConfiguration",
    params: {
      clientResourceId: clientResourceId || readValue(wizard, "ClientResourceId", "clientResourceId") || "",
      resourceConfigurationId: configurationId,
      productResourceKey: readValue(readValue(wizard, "ProductResource", "productResource"), "ProductResourceKey", "productResourceKey") || ""
    }
  };
};

export const buildSourceObjectManagePanelContext = (sourceObject, params = {}) => {
  const sourceObjectId = getId(sourceObject) || readValue(sourceObject, "SourceObjectId", "sourceObjectId") || "";
  if (!sourceObjectId) return null;
  return {
    id: sourceObjectId,
    sourceObjectId,
    sourceSystemId: params.sourceSystemId || readValue(sourceObject, "SourceSystemId", "sourceSystemId") || "",
    clientResourceId: params.clientResourceId || "",
    resourceConfigurationId: params.resourceConfigurationId || ""
  };
};

export const formatCount = (value) => {
  if (value === Number.POSITIVE_INFINITY || normalizeText(value) === "unlimited") return "Unlimited";
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 2147483647) return "Unlimited";
  return Number.isFinite(numeric) ? numeric.toLocaleString() : value || "0";
};

const numberOrDefault = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const isReservedConfiguration = (configuration) => {
  const deleted = readValue(configuration, "IsDeleted", "isDeleted") === true;
  const active = readValue(configuration, "IsActive", "isActive") !== false;
  return active && !deleted;
};

const isActiveOffer = (offer) => readValue(offer, "IsActive", "isActive") !== false && readValue(offer, "IsDeleted", "isDeleted") !== true;

const getProductResourceKey = (wizard) =>
  readValue(wizard, "ProductResourceKey", "productResourceKey")
  || readValue(readValue(wizard, "ProductResource", "productResource"), "ProductResourceKey", "productResourceKey")
  || "";

const getConfigurationGrantOfferId = (configuration) =>
  readValue(configuration, "AllowanceClientProductOfferId", "allowanceClientProductOfferId") || "";

const getConfigurationGrantResourceKey = (configuration) =>
  readValue(configuration, "AllowanceGrantProductResourceKey", "allowanceGrantProductResourceKey") || "";

const UNLIMITED_RESOURCES_OFFER_KEY = "UnlimitedResources";
const UNLIMITED_RESOURCES_DISPLAY_NAME = "Unlimited Resources";
const UNLIMITED_RESOURCES_SKU = "CDX-UL-UR-4C0822AD";

const unlimitedResourcesIdentifierAliases = [
  "enterpriseunlimitedpackage",
  "enterpriseunlimitedpackageoffer",
  "enterpriseunlimitedpackageproductoffer",
  "unlimitedmarketplace",
  "unlimitedmarkeplace",
  "unlimitedmarkplace",
  "unlimitedmarketplaceoffer",
  "unlimitedmarkeplaceoffer",
  "unlimitedmarkplaceoffer",
  "unlimitedmarketplaceproductoffer",
  "unlimitedmarkeplaceproductoffer",
  "unlimitedmarkplaceproductoffer",
  "unlimitedresources",
  "unlimitedresourcesoffer",
  "unlimitedresourcesproductoffer"
];

const isUnlimitedResourcesLabelVariant = (value) => {
  const compact = normalizeText(value).replace(/[^a-z0-9]+/g, "");
  return unlimitedResourcesIdentifierAliases.includes(compact);
};

const cleanUnlimitedLabel = (label) => {
  const text = String(label ?? "").trim();
  if (!text) return label;
  if (isUnlimitedResourcesLabelVariant(text)) return UNLIMITED_RESOURCES_DISPLAY_NAME;
  return text.replace(
    /\bUnlimited\s+(?:Market\s*place|Marketplace|Markeplace|Markplace)(?:\s+(?:Product\s+)?Offer)?\b/gi,
    UNLIMITED_RESOURCES_DISPLAY_NAME
  );
};

const isUnlimitedResourcesOffer = (offerKind, ...identifiers) =>
  Number(offerKind) === 3 || normalizeText(offerKind) === "unlimitedresources" || identifiers.some(isUnlimitedResourcesLabelVariant);

const normalizeProductOfferKey = (productOfferKey, offerKind) =>
  isUnlimitedResourcesOffer(offerKind, productOfferKey)
    ? UNLIMITED_RESOURCES_OFFER_KEY
    : String(productOfferKey ?? "").trim();

const normalizeProductOfferSku = (productOfferSku, productOfferKey, offerKind) =>
  isUnlimitedResourcesOffer(offerKind, productOfferKey)
    ? UNLIMITED_RESOURCES_SKU
    : String(productOfferSku ?? "").trim();

export const buildGrantCheckSummary = (wizard) => {
  const productResourceKey = getProductResourceKey(wizard);
  const clientResource = readValue(wizard, "ClientResource", "clientResource") || {};
  const resourceConfiguration = readValue(wizard, "ResourceConfiguration", "resourceConfiguration") || null;
  const currentConfigurationId = getId(resourceConfiguration);
  const configurations = asArray(readValue(wizard, "ExistingConfigurations", "existingConfigurations")).filter(isReservedConfiguration);
  const offers = asArray(readValue(wizard, "ClientProductOffers", "clientProductOffers")).filter(isActiveOffer);
  const selectedOfferId = getConfigurationGrantOfferId(resourceConfiguration);
  const selectedResourceKey = getConfigurationGrantResourceKey(resourceConfiguration) || productResourceKey;
  const grants = [];

  for (const offer of offers) {
    const offerId = getId(offer);
    const quantity = Math.max(1, numberOrDefault(readValue(offer, "Quantity", "quantity"), 1));
    const offerKind = readValue(offer, "OfferKind", "offerKind");
    const productOfferKey = normalizeProductOfferKey(readValue(offer, "ProductOfferKey", "productOfferKey") || "", offerKind);
    const productOfferSku = normalizeProductOfferSku(readValue(offer, "Sku", "sku") || "", productOfferKey, offerKind);
    const offerName = cleanUnlimitedLabel(getDisplayName(offer) || productOfferKey || "Product Offer");
    const isUnlimited = isUnlimitedResourcesOffer(offerKind, productOfferKey);

    if (isUnlimited) {
      const assigned = configurations.filter((configuration) =>
        getId(configuration) !== currentConfigurationId
        && getConfigurationGrantOfferId(configuration) === offerId).length;
      grants.push({
        id: `${offerId}:${productResourceKey}`,
        clientProductOfferId: offerId,
        productOfferKey,
        productOfferSku,
        name: offerName,
        productResourceKey,
        quantity,
        allowed: Number.POSITIVE_INFINITY,
        assigned,
        remaining: Number.POSITIVE_INFINITY,
        unlimited: true
      });
      continue;
    }

    for (const grant of asArray(readValue(offer, "ResourceGrantsSnapshot", "resourceGrantsSnapshot", "resourceGrants"))) {
      const grantResourceKey = readValue(grant, "ProductResourceKey", "productResourceKey") || "";
      if (normalizeText(grantResourceKey) !== normalizeText(productResourceKey)) continue;
      const allowed = Math.max(0, Math.floor(numberOrDefault(readValue(grant, "AllowedResourceConfigurationCount", "allowedResourceConfigurationCount"), 0) * quantity));
      const assigned = configurations.filter((configuration) =>
        getId(configuration) !== currentConfigurationId
        && getConfigurationGrantOfferId(configuration) === offerId
        && (!getConfigurationGrantResourceKey(configuration)
          || normalizeText(getConfigurationGrantResourceKey(configuration)) === normalizeText(grantResourceKey))).length;
      grants.push({
        id: `${offerId}:${grantResourceKey}`,
        clientProductOfferId: offerId,
        productOfferKey,
        productOfferSku,
        name: offerName,
        productResourceKey: grantResourceKey,
        quantity,
        allowed,
        assigned,
        remaining: Math.max(0, allowed - assigned),
        unlimited: false
      });
    }
  }

  const hasGrantData = grants.length > 0;
  const allowed = hasGrantData
    ? grants.some((grant) => grant.unlimited)
      ? Number.POSITIVE_INFINITY
      : grants.reduce((total, grant) => total + grant.allowed, 0)
    : Math.max(0, numberOrDefault(readValue(readValue(clientResource, "ResourceAllowance", "resourceAllowance"), "AllowedResourceConfigurationCount", "allowedResourceConfigurationCount"), 0));
  const filled = configurations.length;
  const remaining = allowed === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : Math.max(0, allowed - filled);
  const availableGrants = grants.filter((grant) => grant.unlimited || grant.remaining > 0);
  const appliedGrant = grants.find((grant) =>
    grant.clientProductOfferId === selectedOfferId
    && (!selectedResourceKey || normalizeText(grant.productResourceKey) === normalizeText(selectedResourceKey)))
    || null;
  const defaultGrant = availableGrants.length === 1 ? availableGrants[0] : null;
  const selectedGrant = appliedGrant || defaultGrant;
  const unattributedCount = configurations.filter((configuration) => !getConfigurationGrantOfferId(configuration)).length;
  const currentConfigurationIsUnattributed = Boolean(currentConfigurationId) && !selectedOfferId;

  return {
    productResourceKey,
    clientResource,
    resourceConfiguration,
    configurations,
    grants,
    availableGrants,
    selectedGrant,
    appliedGrant,
    defaultGrant,
    selectedOfferId,
    allowed,
    filled,
    remaining,
    hasGrantData,
    hasAppliedGrant: Boolean(appliedGrant),
    usesLegacyFallback: !hasGrantData,
    unattributedCount,
    currentConfigurationIsUnattributed,
    canContinue: Boolean(currentConfigurationId) && (hasGrantData ? Boolean(appliedGrant) : remaining > 0)
  };
};
